import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiDollarSign,
  FiUsers,
  FiAward,
  FiTrendingUp,
  FiTrendingDown,
  FiCheckCircle,
  FiRefreshCw,
  FiShield,
  FiActivity,
} from 'react-icons/fi';
import { Loader, SkeletonCard } from '../Common/Loader';
import { DashboardData, User, CopyRelationship } from '../../types';
import AdminDashboard from '../Admin/AdminDashboard';
import { useApi } from '../../hooks/useApi';
import ProfitLoss from './ProfitLoss';
import OrdersList from '../Trading/OrdersList';
import PaymentDueTimer from '../Common/PaymentDueTimer';
import WebullRiskManagementModal from '../Trading/WebullRiskManagementModal';
import GenesisRiskManagementModal from '../Trading/GenesisRiskManagementModal';
import { isMasterFeeOnlyPlan, isMasterFeeSubscription, isComplimentaryPlan } from '../../utils/subscriptionAccess';
import { showToast } from '../../utils/toast';

interface StatCardProps {
  icon: React.ReactNode;
  title: string;
  value: string | number;
  subtitle: string;
  color?: string;
}

const StatCard: React.FC<StatCardProps> = ({ icon, title, value, subtitle, color = 'primary' }) => {
  const colorClasses = {
    primary: {
      bg: 'bg-gradient-to-br from-primary-500 to-primary-600',
      shadow: 'shadow-primary-500/30 hover:shadow-primary-500/40',
      border: 'border-primary-200/50',
    },
    green: {
      bg: 'bg-gradient-to-br from-success-500 to-success-600',
      shadow: 'shadow-success-500/30 hover:shadow-success-500/40',
      border: 'border-success-200/50',
    },
    blue: {
      bg: 'bg-gradient-to-br from-info-500 to-info-600',
      shadow: 'shadow-info-500/30 hover:shadow-info-500/40',
      border: 'border-info-200/50',
    },
  };

  const selectedColor = colorClasses[color as keyof typeof colorClasses] || colorClasses.primary;

  return (
    <div className={`card-premium group hover:-translate-y-2 hover:shadow-2xl ${selectedColor.shadow} transition-all duration-300 relative overflow-hidden`}>
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-white/10 to-transparent rounded-full blur-2xl"></div>

      <div className="flex items-start gap-4 relative z-10">
        <div className={`lg-p-3 p-1.5 rounded-xl ${selectedColor.bg} text-white shadow-lg ${selectedColor.shadow} group-hover:scale-110 transition-transform duration-300`}>
          {icon}
        </div>
        <div className="flex-1">
          <h4 className="text-sm font-medium text-muted mb-1.5 uppercase whitespace-nowrap tracking-wide">{title}</h4>
          <div className="lg:text-3xl text-xl font-bold text-foreground mb-1.5 tabular-nums">{value}</div>
          <div className="text-sm text-muted flex items-center gap-1">
            {subtitle}
          </div>
        </div>
      </div>
    </div>
  );
};

interface OverviewProps {
  data: DashboardData | null;
  user: User;
  refresh: () => void;
  selectedBrokerId?: string | null;
}

const Overview: React.FC<OverviewProps> = ({ data, user, refresh, selectedBrokerId }) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showWebullRiskModal, setShowWebullRiskModal] = useState(false);
  const [showGenesisRiskModal, setShowGenesisRiskModal] = useState(false);
  const navigate = useNavigate();
  const { data: relationshipsData } = useApi.useCopyRelationships(selectedBrokerId, {
    enabled: user.role === 'follower',
    allBrokers: true,
  });
  const { data: mastersData } = useApi.useMasters(selectedBrokerId, {
    enabled: user.role === 'follower' && isMasterFeeOnlyPlan(String(user.plan || '')),
  });
  const { data: subscriptionData } = useApi.useSubscriptionStatus({
    enabled: user.role === 'follower',
  });
  const createCheckoutMutation = useApi.useCreateCheckoutSession({
    onError: (error: Error) => {
      showToast('error', error.message || 'Failed to start checkout.');
    },
  });
  const cancelSubscriptionMutation = useApi.useCancelSubscription({
    onSuccess: (data) => {
      showToast('success', data?.message || 'Billing cancelled from next month.');
    },
    onError: (error: Error) => {
      showToast('error', error.message || 'Failed to cancel billing.');
    },
  });
  const resumeSubscriptionMutation = useApi.useResumeSubscription({
    onSuccess: (data) => {
      showToast('success', data?.message || 'Billing resumed.');
    },
    onError: (error: Error) => {
      showToast('error', error.message || 'Failed to resume billing.');
    },
  });

  const followerPlan = String(user.plan || '').toLowerCase();
  const isMasterFeePlan = isMasterFeeOnlyPlan(followerPlan);
  const masterFeeSub = isMasterFeeSubscription(subscriptionData?.masterFeeSubscription)
    ? subscriptionData?.masterFeeSubscription
    : isMasterFeeSubscription(subscriptionData?.subscription)
      ? subscriptionData?.subscription
      : null;
  const platformSub = isMasterFeeSubscription(subscriptionData?.subscription)
    ? null
    : subscriptionData?.subscription;
  const showingMasterFee = isMasterFeePlan && !!masterFeeSub?.endDate;
  const paymentDueSub = showingMasterFee ? masterFeeSub : platformSub;
  const cancelAtPeriodEnd = Boolean(paymentDueSub?.cancelAtPeriodEnd);
  const paymentDueLabel = showingMasterFee
    ? cancelAtPeriodEnd
      ? 'Master fee access ends in'
      : 'Days left in billing period'
    : cancelAtPeriodEnd
      ? 'Plan access ends in'
      : 'Days left in billing period';

  const planAmountDue = (() => {
    switch (followerPlan) {
      case 'og':
        return '$99';
      case 'special_users':
        return '$199';
      case 'old_user':
        return '$299';
      case 'full_access_paid':
        return '$399';
      default:
        return null;
    }
  })();

  const paymentAmountDue = planAmountDue;

  const handlePayNow = () => {
    const legacyWhopPlans = new Set(['og', 'special_users', 'old_user', 'full_access_paid']);
    if (legacyWhopPlans.has(followerPlan)) {
      createCheckoutMutation.mutate({ plan: followerPlan as 'og' | 'special_users' | 'old_user' | 'full_access_paid' });
      return;
    }

    if (showingMasterFee) {
      const followedMaster = (mastersData?.masters || []).find((master: { isFollowing?: boolean; _id?: string }) => master.isFollowing);
      const relationships = relationshipsData?.relationships || [];
      const followedRel = relationships.find(
        (relationship: CopyRelationship) =>
          relationship.status === 'active' || relationship.status === 'paused'
      ) || relationships[0];
      const masterRef = followedRel?.master as { _id?: string } | string | undefined;
      const masterId =
        followedMaster?._id ||
        (typeof masterRef === 'string' ? masterRef : masterRef?._id) ||
        user.following?.[0]?.id ||
        '';
      if (!masterId) {
        showToast('error', 'Follow a master first, then pay.');
        navigate('/dashboard/masters');
        return;
      }
      createCheckoutMutation.mutate({ plan: 'masterfee', masterId: String(masterId) });
      return;
    }

    navigate('/dashboard/pricing');
  };

  const handleCancelBilling = () => {
    const ok = window.confirm(
      'Cancel billing from next month?\n\nYou keep access until the current period ends. After that, trading may pause if you do not pay.'
    );
    if (!ok) return;
    cancelSubscriptionMutation.mutate(showingMasterFee ? 'masterfee' : 'platform');
  };

  const handleResumeBilling = () => {
    resumeSubscriptionMutation.mutate(showingMasterFee ? 'masterfee' : 'platform');
  };


  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refresh();
    } finally {
      // Give it a minimum visible time for feedback
      setTimeout(() => setIsRefreshing(false), 500);
    }
  };

  const activeRelationshipsCount = useMemo(() => {
    if (user.role !== 'follower') return 0;
    const relationships = relationshipsData?.relationships || [];
    const activeCount = relationships.filter(
      (relationship: CopyRelationship) => relationship.status === 'active'
    ).length;

    if (activeCount > 0) {
      return activeCount;
    }

    const fallbackFollowing = Array.isArray(data?.user?.following) ? data?.user?.following : [];
    return fallbackFollowing?.length;
  }, [relationshipsData?.relationships, user.role, data?.user?.following]);

  const { data: tradesData, isLoading: tradesLoading } = useApi.useTrades({
    limit: 5,
    type: user.role === 'master' ? 'master' : 'copy',
    brokerId: selectedBrokerId
  }, {
    enabled: user.role === 'master' || user.role === 'follower'
  });

  const followedMasters = useMemo(() => {
    const fromRelationships = (relationshipsData?.relationships || [])
      .filter(
        (relationship: CopyRelationship) =>
          relationship.status === 'active' ||
          relationship.status === 'paused' ||
          relationship.status === 'pending'
      )
      .map((relationship: CopyRelationship) => {
        const followerBroker = (relationship as any).followerBrokerAccountId;
        const brokerName =
          typeof followerBroker === 'object' && followerBroker?.brokerageName
            ? String(followerBroker.brokerageName)
            : null;
        return {
          key: relationship._id,
          id: relationship.master?._id,
          name: relationship.master?.name || 'Master',
          allocationPercent: Number(relationship.allocationPercent) || 0,
          status: relationship.status,
          brokerName,
        };
      });

    if (fromRelationships.length > 0) return fromRelationships;

    return (data?.user?.following || [])
      .filter((follow) => follow?.name || follow?.id)
      .map((follow, index) => ({
        key: follow.relationshipId || follow.id || `follow-${index}`,
        id: follow.id,
        name: follow.name || 'Master',
        allocationPercent: Number(follow.allocationPercent) || 0,
        status: follow.status || 'active',
        brokerName: null as string | null,
      }));
  }, [relationshipsData?.relationships, data?.user?.following]);

  if (!data) {
    return (
      <div className="space-y-8 animate-fade-in">
        {/* Skeleton Header */}
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="h-10 w-64 bg-muted/20 rounded animate-pulse"></div>
            <div className="h-4 w-48 bg-muted/20 rounded animate-pulse"></div>
          </div>
          <div className="h-10 w-24 bg-muted/20 rounded animate-pulse"></div>
        </div>

        {/* Skeleton Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card-premium">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-xl bg-muted/20 animate-pulse"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-3 w-24 bg-muted/20 rounded animate-pulse"></div>
                  <div className="h-8 w-32 bg-muted/20 rounded animate-pulse"></div>
                  <div className="h-3 w-20 bg-muted/20 rounded animate-pulse"></div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Skeleton Activity */}
        <div className="card-premium">
          <div className="h-6 w-40 bg-muted/20 rounded animate-pulse mb-6"></div>
          <div className="flex items-center justify-center h-48">
            <Loader message="Loading dashboard data..." />
          </div>
        </div>
      </div>
    );
  }

  const recentTrades = tradesData?.trades || [];

  const { user: userData, stats } = data;

  const hasActiveCopy = followedMasters.some((follow) => follow.status === 'active');
  const totalAllocation = followedMasters.reduce(
    (sum, follow) => sum + (Number(follow.allocationPercent) || 0),
    0
  );

  // For admin, show dedicated admin dashboard
  if (user.role === 'admin') {
    return <AdminDashboard refresh={refresh} />;
  }

  // Role-specific greeting
  const getRoleGreeting = () => {
    switch (user.role) {
      case 'admin':
        return 'Admin Dashboard';
      case 'master':
        return 'Master Trader Dashboard';
      case 'follower':
        return 'Follower Dashboard';
      default:
        return 'Dashboard Overview';
    }
  };

  // Role-specific stats
  const getStatsForRole = () => {
    const commonStats = [
      <StatCard
        key="balance"
        icon={<FiDollarSign className="h-8 w-8" />}
        title="Account Balance"
        value={`$${userData.accountBalance?.toLocaleString() || '0'}`}
        subtitle={userData.isAlpacaConnected || userData.hasBrokerageConnected ? 'Connected' : 'Not Connected'}
        color="primary"
      />
    ];

    if (user.role === 'admin') {
      return [
        <StatCard
          key="admin-1"
          icon={<FiShield className="h-8 w-8" />}
          title="Admin Panel"
          value="Active"
          subtitle="System Administrator"
          color="blue"
        />,
        <StatCard
          key="admin-2"
          icon={<FiUsers className="h-8 w-8" />}
          title="Total Users"
          value="-"
          subtitle="Platform users"
          color="primary"
        />,
        <StatCard
          key="admin-3"
          icon={<FiActivity className="h-8 w-8" />}
          title="System Status"
          value="Operational"
          subtitle="All services running"
          color="green"
        />,
        <StatCard
          key="admin-4"
          icon={<FiTrendingUp className="h-8 w-8" />}
          title="Total Trades"
          value={stats.totalTrades}
          subtitle="Platform-wide"
          color="primary"
        />
      ];
    }

    if (user.role === 'master') {
      const profitLoss = stats.profitLoss || 0;
      return [
        ...commonStats,
        <StatCard
          key="pnl"
          icon={profitLoss >= 0 ? <FiTrendingUp className="h-8 w-8" /> : <FiTrendingDown className="h-8 w-8" />}
          title="Total P&L"
          value={`$${profitLoss.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          subtitle={profitLoss >= 0 ? 'Profit' : 'Loss'}
          color={profitLoss >= 0 ? 'green' : 'primary'}
        />,
        // <StatCard
        //   key="today-pnl"
        //   icon={(stats.todayPnL || 0) >= 0 ? <FiTrendingUp className="h-8 w-8" /> : <FiTrendingDown className="h-8 w-8" />}
        //   title="Today P&L"
        //   value={`$${(stats.todayPnL || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
        //   subtitle={(stats.todayPnL || 0) >= 0 ? 'Profit' : 'Loss'}
        //   color={(stats.todayPnL || 0) >= 0 ? 'green' : 'primary'}
        // />,
        // <StatCard
        //   key="weekly-pnl"
        //   icon={(stats.weeklyPnL || 0) >= 0 ? <FiTrendingUp className="h-8 w-8" /> : <FiTrendingDown className="h-8 w-8" />}
        //   title="Weekly P&L"
        //   value={`$${(stats.weeklyPnL || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
        //   subtitle={(stats.weeklyPnL || 0) >= 0 ? 'Profit' : 'Loss'}
        //   color={(stats.weeklyPnL || 0) >= 0 ? 'green' : 'primary'}
        // />,
        // <StatCard
        //   key="monthly-pnl"
        //   icon={(stats.monthlyPnL || 0) >= 0 ? <FiTrendingUp className="h-8 w-8" /> : <FiTrendingDown className="h-8 w-8" />}
        //   title="Monthly P&L"
        //   value={`$${(stats.monthlyPnL || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
        //   subtitle={(stats.monthlyPnL || 0) >= 0 ? 'Profit' : 'Loss'}
        //   color={(stats.monthlyPnL || 0) >= 0 ? 'green' : 'primary'}
        // />,
        <StatCard
          key="followers"
          icon={<FiUsers className="h-8 w-8" />}
          title="Followers"
          value={stats.followerCount}
          subtitle="Active followers"
          color="primary"
        />,
        <StatCard
          key="trades"
          icon={<FiTrendingUp className="h-8 w-8" />}
          title="Total Trades"
          value={stats.totalTrades}
          subtitle={`${stats.successfulTrades} successful`}
          color="primary"
        />
      ];
    }

    // Follower stats
    const profitLoss = stats.profitLoss || 0;
    return [
      ...commonStats,
      <StatCard
        key="pnl"
        icon={profitLoss >= 0 ? <FiTrendingUp className="h-8 w-8" /> : <FiTrendingDown className="h-8 w-8" />}
        title="Total P&L"
        value={`$${profitLoss.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
        subtitle={profitLoss >= 0 ? 'Profit' : 'Loss'}
        color={profitLoss >= 0 ? 'green' : 'primary'}
      />,
      // <StatCard
      //   key="today-pnl"
      //   icon={(stats.todayPnL || 0) >= 0 ? <FiTrendingUp className="h-8 w-8" /> : <FiTrendingDown className="h-8 w-8" />}
      //   title="Today P&L"
      //   value={`$${(stats.todayPnL || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
      //   subtitle={(stats.todayPnL || 0) >= 0 ? 'Profit' : 'Loss'}
      //   color={(stats.todayPnL || 0) >= 0 ? 'green' : 'primary'}
      // />,
      // <StatCard
      //   key="weekly-pnl"
      //   icon={(stats.weeklyPnL || 0) >= 0 ? <FiTrendingUp className="h-8 w-8" /> : <FiTrendingDown className="h-8 w-8" />}
      //   title="Weekly P&L"
      //   value={`$${(stats.weeklyPnL || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
      //   subtitle={(stats.weeklyPnL || 0) >= 0 ? 'Profit' : 'Loss'}
      //   color={(stats.weeklyPnL || 0) >= 0 ? 'green' : 'primary'}
      // />,
      // <StatCard
      //   key="monthly-pnl"
      //   icon={(stats.monthlyPnL || 0) >= 0 ? <FiTrendingUp className="h-8 w-8" /> : <FiTrendingDown className="h-8 w-8" />}
      //   title="Monthly P&L"
      //   value={`$${(stats.monthlyPnL || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
      //   subtitle={(stats.monthlyPnL || 0) >= 0 ? 'Profit' : 'Loss'}
      //   color={(stats.monthlyPnL || 0) >= 0 ? 'green' : 'primary'}
      // />,
      <StatCard
        key="following"
        icon={<FiAward className="h-8 w-8" />}
        title="Following"
        value={activeRelationshipsCount || 0}
        subtitle={`${activeRelationshipsCount || 0} active master${activeRelationshipsCount === 1 ? '' : 's'}`}
        color="primary"
      />,
      <StatCard
        key="trades"
        icon={<FiTrendingUp className="h-8 w-8" />}
        title="Total Trades"
        value={stats.totalTrades}
        subtitle={`${stats.successfulTrades} successful`}
        color="primary"
      />
    ];

  };

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header Section */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="lg:text-4xl text-2xl title-page">
            {getRoleGreeting()}
          </h2>
          <p className="text-muted lg:mt-2 mt-1 lg:text-lg text-[14px]">
            {user.role === 'master' && 'Monitor your followers and trading performance'}
            {user.role === 'follower' && 'Track your copy trading performance in real-time'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          {user.role === 'follower' && (
            <button
              type="button"
              onClick={() => navigate('/become-master-trader')}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-primary/30 bg-primary/10 text-primary text-xs font-semibold hover:bg-primary/15"
            >
              <FiAward className="h-3.5 w-3.5" />
              Become Master
            </button>
          )}
          {(user.role === 'follower' || user.role === 'master') && (
            <>
              <button
                type="button"
                onClick={() => setShowWebullRiskModal(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-sky-500/30 bg-sky-500/10 text-sky-800 dark:text-sky-200 text-xs font-semibold hover:bg-sky-500/15"
                title="Webull risk management"
              >
                <FiShield className="h-3.5 w-3.5 shrink-0" />
                Webull {Number.isFinite(Number(user.optionRm)) && Number(user.optionRm) > 0 ? Number(user.optionRm) : 10}%
              </button>
              <button
                type="button"
                onClick={() => setShowGenesisRiskModal(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-emerald-500/30 bg-emerald-500/10 text-emerald-800 dark:text-emerald-200 text-xs font-semibold hover:bg-emerald-500/15"
                title="Genesis risk management"
              >
                <FiShield className="h-3.5 w-3.5 shrink-0" />
                Genesis {Number.isFinite(Number(user.genesisRm)) && Number(user.genesisRm) > 0 ? Number(user.genesisRm) : 100}%
              </button>
            </>
          )}
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="btn-ghost flex items-center gap-2 hover:bg-primary-50 hover:text-primary-600 disabled:opacity-50"
            title="Refresh dashboard data"
          >
            <FiRefreshCw className={`h-5 w-5 ${isRefreshing ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">{isRefreshing ? 'Refreshing...' : 'Refresh'}</span>
          </button>
        </div>
      </div>

      {user.role === 'follower' && !isComplimentaryPlan(followerPlan) && paymentDueSub?.endDate && (
        <PaymentDueTimer
          endDate={paymentDueSub.endDate}
          daysRemaining={paymentDueSub.daysRemaining}
          label={paymentDueLabel}
          amountDue={paymentAmountDue}
          graceDays={2}
          hint="You have only a 2-day grace period after your plan ends. If you don't pay, trading will pause."
          graceHint="Grace period active (2 days after end date). Pay now or trading will pause when grace ends."
          overdueHint="Your trading is paused because of no funds."
          cancelAtPeriodEnd={cancelAtPeriodEnd}
          onCancel={handleCancelBilling}
          cancelLabel="Cancel"
          canceling={cancelSubscriptionMutation.isPending}
          onResume={handleResumeBilling}
          resumeLabel="Resume billing"
          resuming={resumeSubscriptionMutation.isPending}
          onPay={handlePayNow}
          payLabel={
            paymentAmountDue
              ? `Pay ${paymentAmountDue}`
              : showingMasterFee
                ? 'Pay master fee'
                : 'Pay now'
          }
          paying={createCheckoutMutation.isPending}
        />
      )}

      {/* Role-specific Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 animate-slide-up">
        {getStatsForRole()}
      </div>

      {/* Copy Trading Status - Only for followers */}
      {user.role === 'follower' && (
        <div
          className={`card-premium ${hasActiveCopy ? 'border-success/40' : 'border-border'
            } transition-all duration-300`}
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="lg:text-xl text-md font-semibold text-foreground">Copy Trading Status</h3>
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${hasActiveCopy
              ? 'bg-gradient-to-r from-success-500 to-success-600 shadow-lg shadow-success-500/30'
              : 'bg-surface-elevated'
              }`}>
              <div className={`w-2.5 h-2.5 rounded-full ${hasActiveCopy ? 'bg-white animate-pulse-slow' : 'bg-muted'
                }`}></div>
              <span className={`font-semibold text-sm ${hasActiveCopy ? 'text-white' : 'text-muted'
                }`}>
                {hasActiveCopy ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>

          <div className="space-y-4 pt-4 border-t border-border">
            {followedMasters.length > 0 ? (
              <>
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm text-muted uppercase tracking-wide">Masters you follow</p>
                  <p className="text-sm text-muted">
                    Total allocation{' '}
                    <span className="font-semibold text-foreground">{totalAllocation}%</span>
                  </p>
                </div>
                <div className="space-y-2">
                  {followedMasters.map((follow) => (
                    <div
                      key={follow.key}
                      className="flex items-center justify-between gap-3 rounded-lg border border-border bg-surface-elevated px-4 py-3"
                    >
                      <div className="min-w-0">
                        <p className="text-base font-semibold text-foreground truncate">{follow.name}</p>
                        <p className="text-xs text-muted mt-0.5">
                          {follow.status === 'paused'
                            ? 'Paused'
                            : follow.status === 'pending'
                              ? 'Pending'
                              : 'Active'}
                          {follow.brokerName ? ` · ${follow.brokerName}` : ''}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-xl font-bold tabular-nums text-foreground">
                          {follow.allocationPercent}%
                        </p>
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted">
                          Following
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-6">
                <p className="text-muted font-medium">You are not following any masters yet</p>
                <button
                  type="button"
                  onClick={() => navigate('/dashboard/masters')}
                  className="mt-3 text-sm font-semibold text-primary-600 hover:text-primary-500"
                >
                  Browse master traders
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Master Trader Stats */}
      {user.role === 'master' && (
        <div className="card-premium">
          <h3 className="text-xl font-semibold text-foreground mb-6 flex items-center gap-2">
            <FiAward className="h-6 w-6 text-accent-600" />
            Master Trader Performance
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-6 bg-gradient-to-br from-primary-50 to-primary-100/50 dark:from-primary-900/20 dark:to-primary-800/10 rounded-xl border border-primary-200/60 hover:shadow-lg transition-all duration-300">
              <div className="text-xs font-semibold text-primary mb-2 uppercase tracking-wide">
                Active Followers
              </div>
              <div className="text-3xl font-bold text-primary tabular-nums">{stats.followerCount}</div>
              <div className="mt-2 flex items-center gap-1 text-xs text-primary">
                <FiUsers className="h-3 w-3" />
                <span>Following your trades</span>
              </div>
            </div>
            <div className="p-6 bg-gradient-to-br from-success-50 to-success-100/50 dark:from-success-900/20 dark:to-success-800/10 rounded-xl border border-success-200/60 hover:shadow-lg transition-all duration-300">
              <div className="text-xs font-semibold text-success mb-2 uppercase tracking-wide">
                Successful Trades
              </div>
              <div className="text-3xl font-bold text-success tabular-nums">{stats.successfulTrades}</div>
              <div className="mt-2 flex items-center gap-1 text-xs text-success">
                <FiCheckCircle className="h-3 w-3" />
                <span>Profitable executions</span>
              </div>
            </div>
            <div className="p-6 bg-gradient-to-br from-info-50 to-info-100/50 dark:from-info-900/20 dark:to-info-800/10 rounded-xl border border-info-200/60 hover:shadow-lg transition-all duration-300">
              <div className="text-xs font-semibold text-info mb-2 uppercase tracking-wide">Win Rate</div>
              <div className="text-3xl font-bold text-info tabular-nums">{stats.successRate}%</div>
              <div className="mt-2 flex items-center gap-1 text-xs text-info">
                <FiTrendingUp className="h-3 w-3" />
                <span>Success percentage</span>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Recent Trades - Show for Master and Follower only */}
      {(user.role === 'master' || user.role === 'follower') && (
        <div className="card-premium">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
              <FiActivity className="h-6 w-6 text-primary-600" />
              Trade History
            </h3>
            <span className="text-sm text-muted font-medium">
              {user.role === 'master' && 'Your Trades'}
              {user.role === 'follower' && 'Copied Trades'}
            </span>
          </div>
          {tradesLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          ) : recentTrades && Array.isArray(recentTrades) && recentTrades.length > 0 ? (
            <div className="space-y-3">
              <OrdersList selectedBrokerId={selectedBrokerId} />
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="bg-surface w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 border border-border">
                <FiActivity className="h-8 w-8 text-muted" />
              </div>
              <p className="text-muted font-medium">No trades yet</p>
              <p className="text-sm text-muted/80 mt-1">Your trading activity will appear here</p>
            </div>
          )}
        </div>
      )}

      {/* Portfolio charts: import OverviewPortfolioPerformance from ./OverviewPortfolioPerformance when re-enabling */}
      <ProfitLoss user={user} selectedBrokerId={selectedBrokerId ?? null} />

      <WebullRiskManagementModal
        isOpen={showWebullRiskModal}
        onClose={() => setShowWebullRiskModal(false)}
        user={user}
        onSaved={refresh}
      />
      <GenesisRiskManagementModal
        isOpen={showGenesisRiskModal}
        onClose={() => setShowGenesisRiskModal(false)}
        user={user}
        onSaved={refresh}
      />
    </div>
  );
};

export default Overview;
